// BANDERAS DE ADEUDO — una sola carga para toda la consola.
//
// Por qué existe: la insignia de "debe" tiene que acompañar al alumno en TODAS las pantallas
// (Karla vía Carlos, 7-ago-2026: en Kydemy el símbolo existe pero "no los sigue a todas partes").
// Si cada pantalla pidiera su propio adeudo tendríamos el mismo defecto, más N peticiones.
// Aquí se pide UNA vez `GET /letsmove/members/debt-flags` (mapa contact_id → bandera) y cualquier
// avatar de la app lo consulta con `useDebtFlag(id)` sin pedir nada.
//
// 🔒 Alumnos: el portal vive bajo /me/* con otra autenticación y este endpoint exige el permiso
// `letsmove_members:view`, así que un alumno NUNCA puede ver las banderas de otros — el candado
// es del backend, no de la UI. Aquí ni siquiera se dispara la petición si el rol es 'student'.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api, EVENTO_ADEUDOS } from '../services/apiClient';
import { readCache, writeCache } from '../services/localCache';
import { useAuth } from './AuthContext';

/** 'overdue' = se le pasó la fecha (con la prórroga del mes ya aplicada) · 'due' = debe, en plazo. */
export type DebtLevel = 'overdue' | 'due';

export interface DebtFlag {
  level: DebtLevel;
  total: number;
  count: number;
  months: string[];
  oldest_month: string | null;
  late_fees: number;
}

export interface DebtFlagsSummary {
  students: number; overdue: number; due: number; total: number;
}

type FlagMap = Record<string, DebtFlag>;

interface DebtFlagsValue {
  flags: FlagMap;
  /** Fiados del EQUIPO (users), en mapa APARTE: los ids de users y contacts chocan. */
  userFlags: FlagMap;
  summary: DebtFlagsSummary | null;
  /** Aún no llega la primera respuesta: los avatares no pintan insignia todavía. */
  loading: boolean;
  /** Vuelve a pedir el mapa. Llamar tras cobrar/crear/cancelar un adeudo. */
  refresh: () => void;
}

const CACHE_KEY = 'debt-flags-v2';   // v2: + mapa `users` (fiados de maestros)
const EMPTY: FlagMap = {};
const DebtFlagsContext = createContext<DebtFlagsValue>({
  flags: EMPTY, userFlags: EMPTY, summary: null, loading: false, refresh: () => {},
});

/** Ventana mínima entre refrescos automáticos al volver a la pestaña (evita ráfagas). */
const REFRESH_THROTTLE_MS = 60_000;

export function DebtFlagsProvider({ children }: { children: React.ReactNode }) {
  const { user, hasModule } = useAuth();
  // El alumno no pide banderas (no las tendría: el backend le respondería 403).
  // Marketing (admin sin Finanzas, 29-sep-2026) tampoco: la moneda de adeudo es dinero.
  const habilitado = !!user && user.role !== 'student' && hasModule('letsmove_members')
    && (user.role === 'teacher' || hasModule('letsmove_finances') || hasModule('cashbox'));

  const cached = readCache<{ flags: FlagMap; userFlags?: FlagMap; summary: DebtFlagsSummary }>(CACHE_KEY);
  const [flags, setFlags] = useState<FlagMap>(cached?.flags ?? EMPTY);
  const [userFlags, setUserFlags] = useState<FlagMap>(cached?.userFlags ?? EMPTY);
  const [summary, setSummary] = useState<DebtFlagsSummary | null>(cached?.summary ?? null);
  const [loading, setLoading] = useState(habilitado && !cached);
  const ultimo = useRef(0);
  const vivo = useRef(true);

  const cargar = useCallback(async () => {
    if (!habilitado) { setFlags(EMPTY); setSummary(null); setLoading(false); return; }
    ultimo.current = Date.now();
    try {
      const r = await api.get<{ data: FlagMap; users?: FlagMap; summary: DebtFlagsSummary }>('/members/debt-flags');
      if (!vivo.current) return;
      setFlags(r.data || EMPTY);
      setUserFlags(r.users || EMPTY);
      setSummary(r.summary || null);
      writeCache(CACHE_KEY, { flags: r.data || EMPTY, userFlags: r.users || EMPTY, summary: r.summary || null });
    } catch {
      // Sin permiso o backend caído: se queda como está (o vacío). La insignia es informativa;
      // que falle NUNCA debe romper la pantalla que la contiene.
      if (vivo.current && !cached) { setFlags(EMPTY); setUserFlags(EMPTY); setSummary(null); }
    } finally {
      if (vivo.current) setLoading(false);
    }
  // `cached` solo se lee en el arranque; no debe re-disparar la carga.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habilitado]);

  useEffect(() => {
    vivo.current = true;
    cargar();
    return () => { vivo.current = false; };
  }, [cargar]);

  // Al volver a la pestaña se revalida: alguien pudo cobrar en el mostrador mientras tanto.
  useEffect(() => {
    if (!habilitado) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - ultimo.current < REFRESH_THROTTLE_MS) return;
      cargar();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [habilitado, cargar]);

  // Alguien creó, cobró, canceló o borró un adeudo en ESTA pestaña (lo avisa apiClient): la moneda
  // se pone o se quita en el acto. Con espera corta, porque una venta de vitrina manda un POST por
  // artículo y no hace falta pedir el mapa una vez por cada uno.
  useEffect(() => {
    if (!habilitado) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const onCambio = () => { if (t) clearTimeout(t); t = setTimeout(cargar, 400); };
    window.addEventListener(EVENTO_ADEUDOS, onCambio);
    return () => { if (t) clearTimeout(t); window.removeEventListener(EVENTO_ADEUDOS, onCambio); };
  }, [habilitado, cargar]);

  const value = useMemo<DebtFlagsValue>(
    () => ({ flags, userFlags, summary, loading, refresh: cargar }),
    [flags, userFlags, summary, loading, cargar],
  );
  return <DebtFlagsContext.Provider value={value}>{children}</DebtFlagsContext.Provider>;
}

/** Bandera de UN alumno (null = está al corriente, o aún no cargó, o no hay permiso). */
export function useDebtFlag(contactId?: number | string | null): DebtFlag | null {
  const { flags } = useContext(DebtFlagsContext);
  if (contactId === null || contactId === undefined || contactId === '') return null;
  return flags[String(contactId)] ?? null;
}

/** Bandera de UN maestro / persona del equipo (fiados de vitrina), por su users.id. */
export function useUserDebtFlag(userId?: number | string | null): DebtFlag | null {
  const { userFlags } = useContext(DebtFlagsContext);
  if (userId === null || userId === undefined || userId === '') return null;
  return userFlags[String(userId)] ?? null;
}

/** Mapa completo + resumen + refresh. Para paneles de cobranza y para refrescar tras cobrar. */
export function useDebtFlags(): DebtFlagsValue {
  return useContext(DebtFlagsContext);
}
